import { Routes } from '@angular/router';
import { AppLayout } from '@/layout/component/cloud-app.layout';
import { Notfound } from '@/pages/notfound/notfound';
import { Error } from '@/pages/error/error';
import { Dashboard } from './pages/dashboard/dashboard.component';
import { authGuard } from './auth.guard';
import { locationRedirectGuard } from './location-redirect.guard';
import { superAdminGuard } from './super-admin.guard';
import { CloudLogin } from './pages/auth/cloud-login';
import { CloudLogout } from './pages/auth/cloud-logout';
import { OAuthCallback } from './pages/auth/oauth-callback';
import { MobileAuth } from './pages/auth/mobile-auth';
import { OAuthConsent } from './pages/auth/oauth-consent';
import { Device } from './pages/device/device';
import { UserProfile } from './pages/user-profile/user-profile';
import { Logs } from './pages/device/logs/logs';
import { Users } from './pages/users/users';
import { Roles } from './pages/roles/roles';
import { PlatformKeys } from './pages/platform-keys/platform-keys';
import { ServiceDesk } from './pages/service-desk/service-desk';
import { ServiceDeskChat } from './pages/service-desk-chat/service-desk-chat';
import { NamespacesPage } from './pages/namespaces/namespaces';
import { NamespaceDetail } from './pages/namespaces/namespace-detail/namespace-detail';
import { NamespaceMembers } from './pages/namespaces/namespace-detail/namespace-members';
import { NamespaceApiKeys } from './pages/namespaces/namespace-detail/namespace-api-keys';
import { NamespaceDomains } from './pages/namespaces/namespace-detail/namespace-domains';
import { NamespaceMcp } from './pages/namespaces/namespace-detail/namespace-mcp';
import { NamespaceIntegrations } from './pages/namespaces/namespace-detail/namespace-integrations';
import { NamespaceSettings } from './pages/namespaces/namespace-detail/namespace-settings';
import { namespaceAdminGuard } from './namespace-admin.guard';
import { Messages } from './pages/device/messages/messages';
import { DeviceOverview } from './pages/device/overview/device-overview';
import { Config } from './pages/device/config/config';
import { DeviceActions } from './pages/device/actions/device-actions';
import { Packages } from './pages/packages/packages';
import { Firmware } from './pages/firmware/firmware';
import { CloudAutoDiscovery } from './pages/device/autodiscovery/autodiscovery';
import { LinkedDevices } from './pages/device/linked-devices/linked-devices';
import { LinkedDeviceDetail } from './pages/linked-device-detail/linked-device-detail';
import { LocationDetailsComponent } from './components/location-details/location-details.component';
import { CloudRemoteConsoleComponent } from './pages/device/console/cloud-remote-console.component';
import { CloudPingComponent } from './pages/device/ping/cloud-ping.component';
import { CloudPowerComponent } from './pages/device/power/cloud-power.component';
import { CloudPropertyList } from './pages/device/property-list/cloud-property-list';
import { ApplianceStatus } from './pages/device/appliance-status/appliance-status';
import { CloudBacnetComponent } from './pages/device/bacnet/cloud-bacnet.component';
import { CloudMqttComponent } from './pages/device/mqtt/cloud-mqtt.component';
import { CloudFileExplorerComponent } from './pages/device/file-explorer/cloud-file-explorer.component';
import { fileExplorerCanDeactivate } from '@/pages/tools/file-explorer/file-explorer.component';
import { DeviceFirmware } from './pages/device/firmware/device-firmware';
import { CloudCredentials } from './pages/device/credentials/cloud-credentials';
import { CloudUiControl } from './pages/device/ui-control/cloud-ui-control';
import { PackageLinkPage } from './pages/package-link/package-link-page';

export const cloudAppRoutes: Routes = [
    {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
    },
    {
        // Chrome-less full-window touch panel UI (must precede the layout route below)
        path: 'device/:deviceId/ui-control/full',
        component: CloudUiControl,
        canActivate: [authGuard],
        data: { fullWindow: true }
    },
    {
        // One layout for every signed-in page, so the menu, top bar and assistant panel persist across sections
        // instead of being rebuilt (and sliding back into place) on each navigation.
        path: '',
        component: AppLayout,
        canActivate: [authGuard],
        // Check the session on every navigation, as when each section had its own layout route.
        runGuardsAndResolvers: 'always',
        children: [
            {
                path: 'dashboard',
                children: [
                    { path: '', component: Dashboard },
                    { path: ':namespaceId', component: Dashboard },
                    { path: ':namespaceId/:locationId', component: Dashboard }
                ]
            },
            {
                path: 'device',
                children: [
                    {
                        path: ':deviceId',
                        component: Device,
                        children: [
                            { path: '', component: DeviceOverview },
                            { path: 'linked-devices', component: LinkedDevices },
                            { path: 'actions', component: DeviceActions },
                            { path: 'logs', component: Logs },
                            { path: 'messages', component: Messages },
                            { path: 'config', component: Config },
                            { path: 'autodiscovery', component: CloudAutoDiscovery },
                            { path: 'console', component: CloudRemoteConsoleComponent },
                            { path: 'ping', component: CloudPingComponent },
                            { path: 'power', component: CloudPowerComponent },
                            { path: 'property-list', component: CloudPropertyList },
                            { path: 'appliance', component: ApplianceStatus },
                            { path: 'bacnet', component: CloudBacnetComponent },
                            { path: 'mqtt', component: CloudMqttComponent },
                            { path: 'file-explorer', component: CloudFileExplorerComponent, canDeactivate: [fileExplorerCanDeactivate] },
                            { path: 'firmware', component: DeviceFirmware },
                            { path: 'credentials', component: CloudCredentials },
                            { path: 'ui-control', component: CloudUiControl }
                        ]
                    }
                ]
            },
            {
                path: 'linked-device',
                children: [{ path: ':linkedDeviceId', component: LinkedDeviceDetail }]
            },
            {
                path: 'files',
                children: [
                    { path: '', loadComponent: () => import('./pages/files/files').then((module) => module.Files) },
                    { path: ':namespaceId', loadComponent: () => import('./pages/files/files').then((module) => module.Files) },
                    { path: ':namespaceId/:roomId', loadComponent: () => import('./pages/files/files').then((module) => module.Files) }
                ]
            },
            {
                path: 'packages',
                children: [{ path: '', component: Packages }]
            },
            {
                path: 'issues',
                children: [
                    { path: '', loadComponent: () => import('./pages/issues/issues').then((module) => module.Issues) },
                    { path: ':repo/:number', loadComponent: () => import('./pages/issues/issue-detail').then((module) => module.IssueDetail) }
                ]
            },
            {
                path: 'firmware',
                children: [{ path: '', component: Firmware }]
            },
            {
                path: 'location',
                children: [
                    {
                        path: ':id',
                        component: LocationDetailsComponent,
                        canActivate: [locationRedirectGuard],
                        runGuardsAndResolvers: 'always'
                    }
                ]
            },
            {
                // Full-page service desk: spans every tenant the user covers, so the layout hides the tenant switcher.
                path: 'service-desk',
                children: [{ path: '', component: ServiceDeskChat, data: { hideTenantSwitcher: true } }]
            },
            {
                path: 'profile',
                children: [{ path: '', component: UserProfile }]
            },
            {
                path: 'admin',
                children: [
                    { path: 'users', component: Users },
                    { path: 'roles', component: Roles, canActivate: [superAdminGuard] },
                    { path: 'platform-keys', component: PlatformKeys, canActivate: [superAdminGuard] },
                    { path: 'service-desk', component: ServiceDesk, canActivate: [superAdminGuard] },
                    // Tenants (called "namespaces" in the API — the route parameter keeps that name)
                    { path: 'tenants', component: NamespacesPage },
                    {
                        path: 'tenants/:namespaceId',
                        component: NamespaceDetail,
                        canActivate: [namespaceAdminGuard],
                        children: [
                            { path: '', component: NamespaceMembers },
                            { path: 'api-keys', component: NamespaceApiKeys },
                            { path: 'domains', component: NamespaceDomains },
                            { path: 'ai-agents', component: NamespaceMcp },
                            { path: 'integrations', component: NamespaceIntegrations },
                            { path: 'settings', component: NamespaceSettings }
                        ]
                    },
                    // Legacy paths
                    { path: 'namespaces', redirectTo: 'tenants', pathMatch: 'full' },
                    { path: 'namespaces/:namespaceId', redirectTo: 'tenants/:namespaceId', pathMatch: 'full' },
                    { path: 'namespaces/:namespaceId/:child', redirectTo: 'tenants/:namespaceId/:child' },
                    // Namespace management moved from the old Settings page
                    { path: 'settings', redirectTo: 'tenants', pathMatch: 'full' }
                ]
            }
        ]
    },
    // Public package download page opened by short share links (nginx maps /p/{code} here)
    { path: 'p/:code', component: PackageLinkPage },
    { path: 'auth/login', component: CloudLogin },
    { path: 'oauth-callback', component: OAuthCallback },
    // Native-app (iPad panel) sign-in bridge — opened in ASWebAuthenticationSession.
    { path: 'mobile-auth', component: MobileAuth },
    // OAuth consent for AI agents (MCP) — /oauth/authorize on the API sends the browser here.
    { path: 'oauth/consent', component: OAuthConsent, canActivate: [authGuard] },
    // Firebase handles password reset and invites via email links
    // These routes redirect to login where the email link flow is handled
    { path: 'reset-password', redirectTo: 'auth/login', pathMatch: 'full' },
    { path: 'accept-invite', redirectTo: 'auth/login', pathMatch: 'full' },
    { path: 'auth/logout', component: CloudLogout },
    { path: 'error', component: Error },
    { path: 'notfound', component: Notfound },
    { path: '**', component: Notfound }
];
